import React from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Paper, Button } from '@mui/material';
import { useAuth } from '@/hooks/useAuth';
import { useHasAccess } from '@/hooks/useHasAccess';

interface ProtectedRouteProps {
  children: React.ReactNode;
  permissions?: string[];
  requireAll?: boolean; // If true, user must have all permissions. If false, user needs any one permission
  requireStore?: boolean; // If true, user must have an active store
  requireOutlet?: boolean; // If true, user must have an active outlet
  requireRole?: 'STAFF' | 'MANAGER' | 'OWNER'; // Minimum required role in current store
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  permissions = [],
  requireAll = false,
  requireStore = false,
  requireOutlet = false,
  requireRole,
}) => {
  const { isAuthenticated, hasAnyPermission, hasAllPermissions } = useAuth();
  const {
    hasStoreContext,
    hasOutletContext,
    hasCurrentStoreRole,
    stores,
    currentStoreOutlets = [],
  } = useHasAccess();
  const { t } = useTranslation('common');

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check if user has any stores assigned
  if (requireStore && stores.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
        p={3}
      >
        <Paper
          elevation={2}
          sx={{
            p: 4,
            textAlign: 'center',
            maxWidth: 400,
          }}
        >
          <Typography variant="h5" color="error" gutterBottom>
            No Store Assigned
          </Typography>
          <Typography variant="body1" color="text.secondary">
            You are not assigned to any store. Please contact your administrator.
          </Typography>
        </Paper>
      </Box>
    );
  }

  // Check if user has active store context
  if (requireStore && !hasStoreContext()) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
        p={3}
      >
        <Paper
          elevation={2}
          sx={{
            p: 4,
            textAlign: 'center',
            maxWidth: 400,
          }}
        >
          <Typography variant="h5" color="warning.main" gutterBottom>
            No Active Store
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Please select a store to continue.
          </Typography>
          <Button
            variant="contained"
            sx={{ mt: 2 }}
            onClick={() => window.location.reload()}
          >
            Refresh Page
          </Button>
        </Paper>
      </Box>
    );
  }

  // Check if user has any outlets assigned
  if (requireOutlet && currentStoreOutlets.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
        p={3}
      >
        <Paper
          elevation={2}
          sx={{
            p: 4,
            textAlign: 'center',
            maxWidth: 400,
          }}
        >
          <Typography variant="h5" color="error" gutterBottom>
            No Outlet Assigned
          </Typography>
          <Typography variant="body1" color="text.secondary">
            You are not assigned to any outlet in this store. Please contact your
            administrator.
          </Typography>
        </Paper>
      </Box>
    );
  }

  // Check if user has active outlet context
  if (requireOutlet && !hasOutletContext()) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
        p={3}
      >
        <Paper
          elevation={2}
          sx={{
            p: 4,
            textAlign: 'center',
            maxWidth: 400,
          }}
        >
          <Typography variant="h5" color="warning.main" gutterBottom>
            No Active Outlet
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Please select an outlet to continue.
          </Typography>
          <Button
            variant="contained"
            sx={{ mt: 2 }}
            onClick={() => window.location.reload()}
          >
            Refresh Page
          </Button>
        </Paper>
      </Box>
    );
  }

  // Check minimum role requirement
  if (requireRole && !hasCurrentStoreRole(requireRole)) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
        p={3}
      >
        <Paper
          elevation={2}
          sx={{
            p: 4,
            textAlign: 'center',
            maxWidth: 400,
          }}
        >
          <Typography variant="h5" color="error" gutterBottom>
            Insufficient Role
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('errors.requires_role', { role: requireRole })}
          </Typography>
        </Paper>
      </Box>
    );
  }

  // Check permissions
  if (permissions.length > 0) {
    const hasRequiredPermissions = requireAll
      ? hasAllPermissions(permissions)
      : hasAnyPermission(permissions);

    // If user doesn't have required permissions, show access denied
    if (!hasRequiredPermissions) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
          p={3}
        >
          <Paper
            elevation={2}
            sx={{
              p: 4,
              textAlign: 'center',
              maxWidth: 400,
            }}
          >
            <Typography variant="h5" color="error" gutterBottom>
              Access Denied
            </Typography>
            <Typography variant="body1" color="text.secondary">
              You don't have permission to access this page.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Required permissions: {permissions.join(', ')}
            </Typography>
          </Paper>
        </Box>
      );
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;